import { AgnosticCanvasWorkflowBuilderState } from './chatOrder';
import { GroupInsertionData } from 'src/slices/canvas-workflow-builder/types/dataTypes';
import { CanvasWorkflowBuilderState } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import { Action } from 'src/types/actionBuilderApiTypes';
import {
  ActionStepList,
  BaseTransition,
  Nullable,
  Step,
} from 'src/types/workflowBuilderAPITypes';
import {
  articleWasHelpfulCase,
  articleWasNotHelpfulCase,
} from 'src/utils/constants';
import { ActionBuilderActionTypes, StepTypes } from 'src/utils/enums';

// Minimal type for canvas data needed by helper functions
type CanvasWithActionsAndSteps = {
  canvas_action_id_to_action_component: Record<string, ActionStepList>;
} & ({ steps: Record<string, Step> } | { step_map: Record<string, Step> });

/** Returns the action id from canvas_action_id_to_action_component
     given the entry step id of an action */

export const getArticleSuggestionActionKeyId = (
  canvasActionIds: CanvasWorkflowBuilderState['canvas_action_id_to_action_component'],
  actions: Array<Action>,
  entryStepId: string,
) => {
  const articleSuggestionActionId =
    actions?.find(
      (action: Action) =>
        action.action_type === ActionBuilderActionTypes.ARTICLE_SUGGESTION,
    )?.action_id || '';

  const actionIdKeys = Object.keys(canvasActionIds);

  return (
    actionIdKeys.find(
      key =>
        canvasActionIds[key]?.['entry_step_id'] === entryStepId &&
        canvasActionIds[key]?.['action_id'] === articleSuggestionActionId,
    ) || ''
  );
};

/** Returns the action key id for a Dynamic Card action with fallback enabled
    given the entry step id of an action */
export const getDynamicCardWithFallbackActionKeyId = (
  canvasActionIds: CanvasWorkflowBuilderState['canvas_action_id_to_action_component'],
  actions: Array<Action>,
  entryStepId: string,
) => {
  const dynamicCardActionId =
    actions?.find(
      (action: Action) =>
        action.action_type === ActionBuilderActionTypes.DYNAMIC_CARD,
    )?.action_id || '';

  const actionIdKeys = Object.keys(canvasActionIds);

  return (
    actionIdKeys.find(key => {
      const actionComponent = canvasActionIds[key];
      return (
        actionComponent?.['entry_step_id'] === entryStepId &&
        actionComponent?.['action_id'] === dynamicCardActionId &&
        actionComponent?.['transition_branches'] &&
        actionComponent['transition_branches'].length > 0
      );
    }) || ''
  );
};

export const doesActionContainStepWithType = (
  canvasWorkflowBuilder:
    | CanvasWorkflowBuilderState
    | GroupInsertionData
    | AgnosticCanvasWorkflowBuilderState
    | CanvasWithActionsAndSteps,
  actionId: string,
  stepType: StepTypes,
) => {
  const canvasActionIdToComponent =
    canvasWorkflowBuilder.canvas_action_id_to_action_component;
  const stepMap =
    'steps' in canvasWorkflowBuilder
      ? canvasWorkflowBuilder.steps
      : canvasWorkflowBuilder.step_map;
  const stepsInAction = canvasActionIdToComponent[actionId]?.steps;

  if (!stepsInAction) {
    return false;
  }

  return stepsInAction.some(stepId => stepMap[stepId].step_type === stepType);
};

export const getStepWithTypeFromAction = (
  canvasWorkflowBuilder:
    | CanvasWorkflowBuilderState
    | AgnosticCanvasWorkflowBuilderState,
  actionId: string,
  stepType: StepTypes,
) => {
  const canvasActionIdToComponent =
    canvasWorkflowBuilder.canvas_action_id_to_action_component;
  const stepMap = canvasWorkflowBuilder.steps;
  const stepsInAction = canvasActionIdToComponent[actionId].steps;

  const stepId = stepsInAction.find(
    stepId => stepMap[stepId].step_type === stepType,
  );

  return stepId ? stepMap[stepId] : undefined;
};

export const isDynamicArticleSuggestion = (
  canvasWorkflowBuilder:
    | CanvasWorkflowBuilderState
    | GroupInsertionData
    | AgnosticCanvasWorkflowBuilderState
    | CanvasWithActionsAndSteps,
  actionId: string,
): boolean => {
  // if the steps contain a go to intent step, then it's a dynamic article suggestion action
  return doesActionContainStepWithType(
    canvasWorkflowBuilder,
    actionId,
    StepTypes.GO_TO_INTENT,
  );
};

export const showSqueezeForGranularArticleStep = (
  canvasActionIds: CanvasWorkflowBuilderState['canvas_action_id_to_action_component'],
  actions: Array<Action>,
  squeezeStepParentId: string,
  lastStepId: string,
) => {
  const articleSuggestionActionId = actions?.find(
    action =>
      action.action_type ===
      ActionBuilderActionTypes.GRANULAR_ARTICLE_SUGGESTION_V2,
  )?.action_id;

  const actionIdKeys = Object.keys(canvasActionIds);

  const actionKeyId = actionIdKeys.find(
    key =>
      canvasActionIds[key]?.['entry_step_id'] === squeezeStepParentId &&
      canvasActionIds[key]?.['action_id'] === articleSuggestionActionId,
  );

  if (actionKeyId) {
    const transitionBranches =
      canvasActionIds[actionKeyId]?.['transition_branches'] || [];
    if (
      transitionBranches.length === 1 &&
      transitionBranches[0]?.parent_step_id === lastStepId
    ) {
      return false;
    }
  }
  return true;
};

/** Returns the step id we need to transition to inside the Article Case Selector */

export const getStepIdToTransitionFromBranch = (
  canvasWorkflowBuilder:
    | CanvasWorkflowBuilderState
    | AgnosticCanvasWorkflowBuilderState,
  actionId: string,
  isSuccessCase: boolean,
): string | null | undefined => {
  const canvasActionIdToComponent =
    canvasWorkflowBuilder.canvas_action_id_to_action_component;
  const condition = isSuccessCase
    ? articleWasHelpfulCase
    : articleWasNotHelpfulCase;

  const transitionBranches =
    canvasActionIdToComponent[actionId]?.['transition_branches'] || [];

  const buttonParentStepid = transitionBranches.find(
    (branch: { display_name: string; parent_step_id: string }) =>
      branch.display_name === condition,
  )?.parent_step_id;

  if (!buttonParentStepid) {
    return null;
  } else {
    if (isSuccessCase) {
      const stepTransitions =
        canvasWorkflowBuilder.steps[buttonParentStepid]?.transitions;
      if (stepTransitions?.length > 1) {
        const validStepTransition = getValidStepTransition(stepTransitions);
        if (!!validStepTransition && !!validStepTransition.step_id) {
          return validStepTransition.step_id;
        }
      }
      return null;
    }
    return buttonParentStepid;
  }
};

export const getStepIdToTransitionForDynamicArticleSuggestion = (
  canvasWorkflowBuilder:
    | CanvasWorkflowBuilderState
    | AgnosticCanvasWorkflowBuilderState,
  actionId: string,
  isBranchChosen: boolean,
  isYesBranchChosen: boolean,
): string | null | undefined => {
  const canvasActionIdToComponent =
    canvasWorkflowBuilder.canvas_action_id_to_action_component;
  const transitionBranches =
    canvasActionIdToComponent[actionId]?.['transition_branches'] || [];

  const transitionParentStep =
    canvasWorkflowBuilder.steps[
      transitionBranches?.find(transition => transition.display_name === 'No')
        ?.parent_step_id || ''
    ];

  // feedback button is enabled
  if (transitionParentStep.step_type === StepTypes.BUTTONS) {
    if (isYesBranchChosen) {
      return transitionParentStep.transitions.find(
        transition =>
          transition.condition_expression?.values &&
          transition.condition_expression?.values[0] === 'yes_resolve',
      )?.step_id;
    } else if (isBranchChosen) {
      return transitionParentStep.transitions.find(
        transition =>
          transition.condition_expression?.values &&
          transition.condition_expression?.values[0] === 'no_resolve',
      )?.step_id;
    } else {
      return null;
    }
  } else if (transitionParentStep.step_type === StepTypes.CONDITION) {
    {
      const nextStep = transitionParentStep?.transitions.filter(
        transition => transition.condition_expression === null,
      );

      return nextStep ? nextStep[0]?.step_id : null;
    }
  }
};

export const getIsTerminalAction = (
  stepId: string | null,
  canvasActionIdToComponent: CanvasWorkflowBuilderState['canvas_action_id_to_action_component'],
) => {
  return Object.values(canvasActionIdToComponent).some(
    action => action.entry_step_id === stepId && action.is_last_element,
  );
};

/** Returns the step id to transition to for Dynamic Card with fallback
 * isCardSelectedBranch: true for "Card Selected" branch, false for "Fallback" branch
 */
export const getStepIdToTransitionForDynamicCardFallback = (
  canvasWorkflowBuilder:
    | CanvasWorkflowBuilderState
    | AgnosticCanvasWorkflowBuilderState,
  actionId: string,
  isBranchChosen: boolean,
  isCardSelectedBranch: boolean,
): string | null | undefined => {
  if (!isBranchChosen) {
    return null;
  }

  const canvasActionIdToComponent =
    canvasWorkflowBuilder.canvas_action_id_to_action_component;
  const entryStepId = canvasActionIdToComponent[actionId]?.entry_step_id;

  if (!entryStepId) {
    return null;
  }

  // Get the entry step (both branches share the same parent_step_id which is the entry step)
  const steps =
    'steps' in canvasWorkflowBuilder
      ? canvasWorkflowBuilder.steps
      : (canvasWorkflowBuilder as AgnosticCanvasWorkflowBuilderState).steps;

  const entryStep = steps[entryStepId];
  if (!entryStep?.transitions?.length) {
    return null;
  }

  // Find the correct transition based on branch selection:
  // - Card Selected: catch-all transition (no condition_expression)
  // - Fallback: transition with condition_expression
  const transition = isCardSelectedBranch
    ? entryStep.transitions.find(
        t =>
          t.condition_expression === null ||
          t.condition_expression === undefined,
      )
    : entryStep.transitions.find(
        t =>
          t.condition_expression !== null &&
          t.condition_expression !== undefined,
      );

  return transition?.step_id ?? null;
};

const getValidStepTransition = (
  stepTransitions: Nullable<BaseTransition>[],
) => {
  return stepTransitions.find((t: Nullable<BaseTransition>) => {
    if (!!t.condition_expression?.values?.length) {
      return t.condition_expression?.values?.[0] === 'yes_resolve';
    } else {
      return true;
    }
  });
};
